import ExperimentProviderContext from '@carvana/experiment/ExperimentProviderContext';
import { useClientSide } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import SpinnerContext from '~/context/vdp/SpinnerContext';
import {
  useGetVehicleIdAndStockNumber,
  useGetVehicleInfoForSpinner
} from '~/context/vdp/VehicleContext';
import type { SpinnerType } from '~/contracts/vdp';
import { navPerformanceNow } from '~/utilities/navigationTiming';
import { useSpinnerConfig } from './hooks';
import SpinnerErrorBoundary from './SpinnerErrorBoundary';

export default function SpinnerLoaderMfe() {
  const { experiments } = useContext(ExperimentProviderContext);
  const { spinnerReady, setIsSpinnerScriptInserted, setSpinnerType } =
    useContext(SpinnerContext);
  const { stockNumber, vehicleId } = useGetVehicleIdAndStockNumber();
  const vehicleInfo = useGetVehicleInfoForSpinner();
  const shouldLoadSpinner = useClientSide();
  const spinnerConfig = useSpinnerConfig();
  const spinnerLoadTimes = useRef<Record<string, number>>({});

  const { setLoading } = spinnerReady;

  const onLoadTimeEvent = useCallback(
    (eventName: string, readyForInteraction = false) => {
      spinnerLoadTimes.current[eventName] = navPerformanceNow();
      if (readyForInteraction)
        Object.keys(spinnerLoadTimes.current).forEach(key => {
          datadogRum.addAction(key, { value: spinnerLoadTimes.current[key] });
        });
    },
    []
  );

  const onLoading = useCallback(() => {
    setIsSpinnerScriptInserted(true);
  }, [setIsSpinnerScriptInserted]);

  const onSetSpinnerType = useCallback(
    (type: SpinnerType) => {
      setSpinnerType(type);
    },
    [setSpinnerType]
  );

  useEffect(() => {
    if (shouldLoadSpinner) setLoading();
  }, [setLoading, shouldLoadSpinner]);

  useEffect(() => {
    onLoadTimeEvent('vdpMfeSpinnerLoaderLoadStarted');
    MfeLoad({
      files: [
        [
          'SpinnerLoaderModule',
          [
            {
              component: 'default',
              default: {
                file: 'index.js',
                manifest: `${
                  process.env.CVNA_APP_CDN_URL
                }/spinner/spinner-loader/assets-manifest.json?v=${getMfeCacheBuster()}`
              }
            }
          ]
        ]
      ]
    }).then(() => {
      onLoadTimeEvent('vdpMfeSpinnerLoaderLoadCompleted');
    });
  }, [onLoadTimeEvent]);

  useEffect(() => {
    onLoadTimeEvent('vdpSpinnerLoaderComponentLoaded');
  }, [onLoadTimeEvent]);

  return shouldLoadSpinner ? (
    <MfeReactComponent
      experiments={experiments}
      module="SpinnerLoaderModule"
      component="default"
      onError={spinnerReady.setError}
      onLoading={onLoading}
      loadTimeEvent={onLoadTimeEvent}
      setSpinnerType={onSetSpinnerType}
      stockNumber={stockNumber}
      vehicleInfo={vehicleInfo}
      vehicleId={vehicleId}
      spinnerConfig={spinnerConfig}
      ErrorNode={SpinnerErrorBoundary}
    />
  ) : null;
}
